import { z } from 'zod';
export const optionLifecycleTermsSchema = z.object({
    /** Canonical product root that owns the verified lifecycle terms. */
    optionRoot: z.string(),
    /** Provider/OCC root used to build quote and order symbols when it differs from the canonical product root. */
    executionOptionRoot: z.string().optional(),
    effectiveFrom: z.string(),
    effectiveTo: z.string().nullable(),
    exerciseStyle: z.string(),
    settlementStyle: z.string(),
    settlementMethod: z.string(),
    standardContractMultiplier: z.number().int().positive().nullable(),
    settlementReferenceRule: z.string().nullable()
});
/**
 * One contract from the Rust option finder's `/find-option`, `/find-multiple-options` and `/option-search` responses:
 * `EnhancedOptionResponse` in `project314-backend/rust/option_finder/src/api/response_types.rs`, which owns the field
 * names. Parse finder payloads with this schema instead of asserting a type. Optional fields are omitted by the finder
 * when it has no value.
 */
export const optionFinderOptionSchema = z.object({
    id: z.number().int().nonnegative(),
    symbol: z.string().min(1),
    underlyingSymbol: z.string().optional(),
    /** Converts the canonical index level into this product's strike coordinate. */
    underlyingPriceMultiplier: z.number().positive().optional(),
    strike: z.number().positive(),
    /** `YYYY-MM-DD`, optionally followed by a time. */
    expirationDate: z.string().regex(/^\d{4}-\d{2}-\d{2}/),
    type: z.enum(['c', 'p']),
    contractSize: z.number().int().positive().optional(),
    /** Required by historical trade replay; absent metadata does not imply supported terms. */
    lifecycleTerms: optionLifecycleTermsSchema.optional(),
    description: z.string().optional(),
    expirationTimestamp: z.number().int().optional(),
    /** Verified settlement-reference timestamp when resolved by the timing-rule service. */
    settlementTimestamp: z.number().int().optional(),
    /** Verified final tradable timestamp for the applicable execution context. */
    lastTradeTimestamp: z.number().int().optional(),
    firstQuoteTimestamp: z.number().int().optional(),
    /** The executable quote the contract was priced at. Present on historical selections; live provider paths may omit it. */
    entryQuoteTimestamp: z.number().int().optional(),
    /**
     * Optional in the Rust type, but every selection comes from a chain whose contracts carry their underlying asset, and
     * settlement timing cannot resolve a contract without it.
     */
    assetId: z.number().int().positive(),
    daysToExpiration: z.number().int().nonnegative(),
    tradingDaysToExpiration: z.number().int().nonnegative(),
    bidPrice: z.number().nonnegative(),
    askPrice: z.number().nonnegative(),
    mid: z.number().nonnegative(),
    last: z.number().optional(),
    volume: z.number().int().nonnegative().optional(),
    bidSize: z.number().int().nonnegative().optional(),
    askSize: z.number().int().nonnegative().optional(),
    openInterest: z.number().int().nonnegative().optional(),
    midIv: z.number().optional(),
    delta: z.number().optional(),
    gamma: z.number().optional(),
    theta: z.number().optional(),
    vega: z.number().optional(),
    underlyingPrice: z.number().positive(),
    /** Annualized return on risk capital as a fraction (1.25 = 125%). */
    premiumArorc: z.number(),
    /** Black-Scholes probability the contract expires out of the money, from 0 to 1. */
    probabilityOTM: z.number().min(0).max(1),
    /** The probability used the default risk-free rate because no treasury yield was available for the entry date. */
    probabilityOtmRateFallback: z.boolean(),
    isMoneyness: z.enum(['ITM', 'OTM', 'ATM']),
    percentageOTM: z.number(),
    percentageITM: z.number(),
    /** `probabilityOTM × premiumArorc × 100`. */
    putSellScore: z.number()
});
//# sourceMappingURL=AssetOptionTypes.js.map